import { MouseEvent, RefObject, useEffect } from "react";

// What acts on its own inside a row, so a click on it never runs the row's action (TABLE-R32): its
// controls and the detail panel under it.
const controlSelector =
  "a, button, input, select, textarea, label, [role='button'], [data-row-detail]";

// A click that ends a text selection is the admin copying, not opening the row.
const isEndingTextSelection = () => (window.getSelection?.()?.toString() ?? "") !== "";

const isClickOnControl = (event: MouseEvent<HTMLElement>) => {
  const target = event.target as Element;
  const control = target.closest(controlSelector);

  return control !== null && event.currentTarget.contains(control);
};

// A click on a row, anywhere but on one of its controls, runs the action for that row.
export const rowClickHandler = <Row>(row: Row, onRowActivate?: (row: Row) => void) => {
  if (!onRowActivate) return undefined;

  return (event: MouseEvent<HTMLElement>) => {
    if (isClickOnControl(event) || isEndingTextSelection()) return;

    onRowActivate(row);
  };
};

// Focusing the selected row also scrolls it into view (TABLE-R33).
export const useSelectedRowFocus = (
  selectedRowRef: RefObject<HTMLElement>,
  isSelectedRowFocused: boolean,
  selectedRowKey?: string
) => {
  useEffect(() => {
    if (!isSelectedRowFocused) return;

    selectedRowRef.current?.focus();
  }, [selectedRowRef, isSelectedRowFocused, selectedRowKey]);
};
